import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Bot, Calendar, Sparkles, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { createPageUrl } from '@/utils';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export default function BotTaskGenerationSection({ activity, userEmail }) {
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState(new Date());
  
  // Check if user is accountable and responsible is a bot
  const isAccountable = activity.accountable_email === userEmail;
  
  const generateTasksMutation = useMutation({
    mutationFn: async (date) => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const response = await base44.functions.invoke('generateTasks', {
        activity_id: activity.id,
        date: dateStr,
        mode: 'generate',
        target: 'bot'
      });
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
      alert(`Successfully generated ${data.tasks_generated} tasks for approval!`);
    },
    onError: (error) => {
      alert(`Failed to generate tasks: ${error.response?.data?.error || error.message}`);
    }
  });

  if (!isAccountable) {
    return null;
  }

  return (
    <Card className="glass-panel border-purple-500/30 bg-purple-900/10">
      <CardHeader>
        <CardTitle className="text-slate-100 text-lg flex items-center gap-2">
          <Bot className="w-5 h-5 text-purple-400" />
          AI Officer Task Generation
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="text-sm text-slate-300">
          Generate daily tasks for your AI Officer to execute. Tasks will be pending your approval.
        </div>
        
        <div className="flex items-center gap-3">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="bg-slate-950 border-slate-700 text-slate-100">
                <Calendar className="w-4 h-4 mr-2" />
                {format(selectedDate, 'MMM d, yyyy')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-white border-slate-300" align="start">
              <CalendarPicker
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          <Button
            onClick={() => generateTasksMutation.mutate(selectedDate)}
            disabled={generateTasksMutation.isPending}
            className="bg-purple-600 hover:bg-purple-700 text-white">
            <Sparkles className="w-4 h-4 mr-2" />
            {generateTasksMutation.isPending ? 'Generating...' : 'Generate Tasks'}
          </Button>
        </div>

        <div className="pt-3 border-t border-slate-700">
          <a
            href={`${createPageUrl('dailytasks')}?date=${format(selectedDate, 'yyyy-MM-dd')}&user=${activity.responsible_email}`}
            className="text-sm text-indigo-400 hover:text-indigo-300 flex items-center gap-1">
            <ExternalLink className="w-3 h-3" />
            View & approve tasks in Daily Task Planner
          </a>
        </div>
      </CardContent>
    </Card>
  );
}